/**
 * TriageStatsSection — panel analítico de TRIAGE para el dashboard del supervisor.
 *
 * Muestra, para un rango de fechas: cuántos pacientes clasificó el triage y en
 * qué NIVEL de urgencia (medidor de acuidad), la proporción de salidas clínicas
 * (911 / presencial / teleconsulta), y los casos que no completaron o no
 * pudieron entrar (región / cola) + cambios de región.
 *
 * Lee los agregados diarios `triage_stats_daily` y `region_stats_daily`
 * (escritos por el backend) con una consulta de rango sobre `fecha` (sin índice
 * compuesto). Solo admin/supervisor (las reglas cierran esas colecciones).
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot, getDocs, limit, Timestamp } from 'firebase/firestore';
import {
  Activity, AlertTriangle, Video, LogOut, MapPinOff, Repeat, Stethoscope, TrendingUp, Clock3,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { db } from '../../config/firebase';
import { MapaCalor, filasDeMapa } from './MapaCalor';
import s from './TriageStatsSection.module.css';

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** Rango máximo consultable (los agregados son un doc por día). */
const MAX_DIAS_RANGO = 92;

// Escala de acuidad clínica: rojo (más urgente) → verde (menos urgente).
// Se define aquí arriba (antes solo se usaba al final del archivo) porque
// SERIES_NIVELES la necesita para construir sus series por hora.
const NIVELES: { nivel: number; nombre: string; color: string }[] = [
  { nivel: 1, nombre: 'Emergencia', color: '#dc2626' },
  { nivel: 2, nombre: 'Urgente', color: '#f97316' },
  { nivel: 3, nombre: 'Prioritario', color: '#f59e0b' },
  { nivel: 4, nombre: 'Estándar', color: '#3b82f6' },
  { nivel: 5, nombre: 'No urgente', color: '#22c55e' },
];

interface TriageDaily {
  fecha: string;
  total?: number;
  porResultado?: Record<string, number>;
  porNivel?: Record<string, number>;
  /** Buckets por hora local de Panamá ('00'..'23') → conteo por salida. */
  porHora?: Record<string, Record<string, number>>;
  /** Buckets por hora → nivel (solo salidas clínicas, que son las únicas con nivel). */
  porHoraNivel?: Record<string, Record<string, number>>;
}
interface RegionDaily { fecha: string; cambios?: number; porHora?: Record<string, number> }

function diasEntre(desde: string, hasta: string): number {
  const [y1, m1, d1] = desde.split('-').map(Number);
  const [y2, m2, d2] = hasta.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000) + 1;
}

function labelDia(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

/** Eventos crudos leídos como RESPALDO para la vista horaria: los buckets
 *  `porHora` de los agregados solo existen desde que el backend los escribe;
 *  para hoy y para días históricos se reconstruye la hora desde el
 *  `creadoEn` de `triage_eventos` / `region_cambios` (legibles por
 *  admin/supervisor según las rules). */
interface EventosCrudos {
  eventos: Array<{ fecha: string; hora: string; resultado: string; nivel: number | null }>;
  cambios: Array<{ fecha: string; hora: string }>;
}

function horaPanama(t: Timestamp | undefined): string | null {
  const d = t?.toDate?.();
  if (!d) return null;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Panama', hour: '2-digit', hour12: false })
    .format(d).slice(0, 2);
}

type PuntoSerie = Record<string, number | string>;

function puntoVacio(etiqueta: string): PuntoSerie {
  return { etiqueta, 'No completaron': 0, 'No pudieron entrar': 0, 'Cambios de región': 0, 'Teleconsulta': 0 };
}

function sumarResultado(p: PuntoSerie, resultado: string, n: number): void {
  if (resultado === 'abandono') p['No completaron'] = (p['No completaron'] as number) + n;
  else if (resultado === 'region_no_habilitada' || resultado === 'cola_llena') {
    p['No pudieron entrar'] = (p['No pudieron entrar'] as number) + n;
  } else if (resultado === 'teleconsulta') p['Teleconsulta'] = (p['Teleconsulta'] as number) + n;
}

/** Suma un mapa plano de conteos (region.porHora, porNivel, etc). */
function sumaValores(o: Record<string, number> | undefined): number {
  return Object.values(o ?? {}).reduce((a, b) => a + (b || 0), 0);
}

/** Suma SOLO las salidas que cuentan para el gráfico de "Evolución" (no
 *  completaron / no pudieron entrar / teleconsulta — emergencia_911 y
 *  presencial quedan fuera, esas las cubre el gráfico de niveles). */
function sumaPorResultadoRelevante(pr: Record<string, number> | undefined): number {
  return (pr?.abandono || 0) + (pr?.region_no_habilitada || 0) + (pr?.cola_llena || 0) + (pr?.teleconsulta || 0);
}
function sumaPorHoraResultadoRelevante(porHora: Record<string, Record<string, number>> | undefined): number {
  let s = 0;
  for (const porRes of Object.values(porHora ?? {})) s += sumaPorResultadoRelevante(porRes);
  return s;
}
function sumaPorHoraNivel(porHoraNivel: Record<string, Record<string, number>> | undefined): number {
  let s = 0;
  for (const porNiv of Object.values(porHoraNivel ?? {})) s += sumaValores(porNiv);
  return s;
}

/** Distribución 0-23h de TODO el rango. `usarBuckets` lo decide el llamador
 *  (comparando el total de los buckets `porHora` contra el total AUTORITATIVO
 *  de `porResultado`/`cambios`, que siempre está completo): si los buckets no
 *  cubren todos los eventos del rango — p. ej. el día en que se activó esta
 *  métrica, con eventos de antes y de después del deploy — se usan ENTERAS
 *  las dos fuentes por separado, nunca mezcladas (mezclar arriesga contar
 *  doble los eventos que sí están en el bucket). Sirve tanto de serie
 *  principal (rango de 1 día) como de segundo gráfico "a qué hora ocurren"
 *  en rangos multi-día. */
function distribucionHoraria(
  triageDocs: TriageDaily[],
  regionDocs: RegionDaily[],
  crudos: EventosCrudos | null,
  usarBuckets: boolean,
): PuntoSerie[] {
  const puntos = Array.from({ length: 24 }, (_, h) => puntoVacio(`${String(h).padStart(2, '0')}:00`));
  if (usarBuckets) {
    for (const t of triageDocs) {
      for (const [hh, porRes] of Object.entries(t.porHora ?? {})) {
        const idx = Number(hh);
        if (!(idx >= 0 && idx <= 23)) continue;
        for (const [res, n] of Object.entries(porRes)) {
          if ((n || 0) > 0) sumarResultado(puntos[idx], res, n);
        }
      }
    }
    for (const r of regionDocs) {
      for (const [hh, n] of Object.entries(r.porHora ?? {})) {
        const idx = Number(hh);
        if (idx >= 0 && idx <= 23 && (n || 0) > 0) {
          puntos[idx]['Cambios de región'] = (puntos[idx]['Cambios de región'] as number) + n;
        }
      }
    }
    return puntos;
  }
  if (!crudos) return puntos;
  // Buckets incompletos para el rango → reconstruir TODO desde los eventos
  // crudos (hora del creadoEn), no solo el faltante.
  for (const e of crudos.eventos) {
    const idx = Number(e.hora);
    if (idx >= 0 && idx <= 23) sumarResultado(puntos[idx], e.resultado, 1);
  }
  for (const c of crudos.cambios) {
    const idx = Number(c.hora);
    if (idx >= 0 && idx <= 23) puntos[idx]['Cambios de región'] = (puntos[idx]['Cambios de región'] as number) + 1;
  }
  return puntos;
}

/** Serie temporal principal con granularidad ADAPTATIVA:
 *  1 día → por hora · hasta 14 días → por día · más → por semana. */
function construirSerie(
  desde: string,
  hasta: string,
  triageDocs: TriageDaily[],
  regionDocs: RegionDaily[],
  crudos: EventosCrudos | null,
  usarBucketsHora: boolean,
): { granularidad: 'hora' | 'dia' | 'semana'; puntos: PuntoSerie[] } {
  const nDias = diasEntre(desde, hasta);
  const porFecha = new Map(triageDocs.map((d) => [d.fecha, d]));
  const regionPorFecha = new Map(regionDocs.map((d) => [d.fecha, d]));

  const metricasDe = (t: TriageDaily | undefined, r: RegionDaily | undefined) => ({
    'No completaron': t?.porResultado?.abandono ?? 0,
    'No pudieron entrar': (t?.porResultado?.region_no_habilitada ?? 0) + (t?.porResultado?.cola_llena ?? 0),
    'Cambios de región': r?.cambios ?? 0,
    'Teleconsulta': t?.porResultado?.teleconsulta ?? 0,
  });

  if (nDias <= 1) {
    return { granularidad: 'hora', puntos: distribucionHoraria(triageDocs, regionDocs, crudos, usarBucketsHora) };
  }

  // Lista de fechas del rango (ISO) en orden.
  const fechas: string[] = [];
  for (let f = desde; f <= hasta && fechas.length <= MAX_DIAS_RANGO; f = addDays(f, 1)) fechas.push(f);

  if (nDias <= 14) {
    // Por DÍA.
    const puntos = fechas.map((f) => ({
      etiqueta: labelDia(f),
      ...metricasDe(porFecha.get(f), regionPorFecha.get(f)),
    }));
    return { granularidad: 'dia', puntos };
  }

  // Por SEMANA: bloques de 7 días desde el inicio del rango.
  const puntos: PuntoSerie[] = [];
  for (let i = 0; i < fechas.length; i += 7) {
    const bloque = fechas.slice(i, i + 7);
    const acc = puntoVacio(`Sem ${labelDia(bloque[0])}`);
    for (const f of bloque) {
      const m = metricasDe(porFecha.get(f), regionPorFecha.get(f));
      acc['No completaron'] = (acc['No completaron'] as number) + m['No completaron'];
      acc['No pudieron entrar'] = (acc['No pudieron entrar'] as number) + m['No pudieron entrar'];
      acc['Cambios de región'] = (acc['Cambios de región'] as number) + m['Cambios de región'];
      acc['Teleconsulta'] = (acc['Teleconsulta'] as number) + m['Teleconsulta'];
    }
    puntos.push(acc);
  }
  return { granularidad: 'semana', puntos };
}

/** Colores de las series — mismos que las mini-tarjetas de abajo. */
const SERIES_TEMPORALES = [
  { key: 'No completaron', color: '#64748b' },
  { key: 'No pudieron entrar', color: '#8b5cf6' },
  { key: 'Cambios de región', color: '#2563eb' },
  { key: 'Teleconsulta', color: '#16a34a' },
] as const;

/** Series del gráfico horario por NIVEL de urgencia — mismos colores que el
 *  medidor de acuidad (rojo→verde), una barra apilada por hora del día. */
const SERIES_NIVELES = NIVELES.map((n) => ({ key: `N${n.nivel} · ${n.nombre}`, color: n.color }));
/** Nivel numérico ('1'..'5') → clave de serie, para sumar los buckets. */
const NIVEL_A_SERIE = new Map(NIVELES.map((n) => [String(n.nivel), SERIES_NIVELES[n.nivel - 1].key]));

function puntoVacioNivel(etiqueta: string): PuntoSerie {
  const p: PuntoSerie = { etiqueta };
  for (const s of SERIES_NIVELES) p[s.key] = 0;
  return p;
}

/** Distribución 0-23h de TODO el rango, por NIVEL de urgencia — mismo patrón
 *  que `distribucionHoraria`: `usarBuckets` lo decide el llamador comparando
 *  el total de `porHoraNivel` contra el total autoritativo de `porNivel`. */
function distribucionHorariaPorNivel(
  triageDocs: TriageDaily[],
  crudos: EventosCrudos | null,
  usarBuckets: boolean,
): PuntoSerie[] {
  const puntos = Array.from({ length: 24 }, (_, h) => puntoVacioNivel(`${String(h).padStart(2, '0')}:00`));
  if (usarBuckets) {
    for (const t of triageDocs) {
      for (const [hh, porNiv] of Object.entries(t.porHoraNivel ?? {})) {
        const idx = Number(hh);
        if (!(idx >= 0 && idx <= 23)) continue;
        for (const [niv, n] of Object.entries(porNiv)) {
          const key = NIVEL_A_SERIE.get(niv);
          if (key && (n || 0) > 0) puntos[idx][key] = (puntos[idx][key] as number) + n;
        }
      }
    }
    return puntos;
  }
  if (!crudos) return puntos;
  // Buckets incompletos → reconstruir TODO desde los eventos crudos.
  for (const e of crudos.eventos) {
    if (e.nivel == null) continue;
    const idx = Number(e.hora);
    const key = NIVEL_A_SERIE.get(String(e.nivel));
    if (key && idx >= 0 && idx <= 23) puntos[idx][key] = (puntos[idx][key] as number) + 1;
  }
  return puntos;
}

/** Barras APILADAS por bucket (hora/día/semana) — comunican mejor los
 *  conteos discretos que las líneas: la altura total es el volumen del
 *  bucket y los segmentos muestran la composición por métrica. */
function GraficaApilada({
  puntos,
  series = SERIES_TEMPORALES,
  alto = 220,
}: {
  puntos: PuntoSerie[];
  series?: readonly { key: string; color: string }[];
  alto?: number;
}) {
  return (
    <div className={s.serieChart}>
      <ResponsiveContainer width="100%" height={alto}>
        <BarChart data={puntos} margin={{ top: 6, right: 12, bottom: 0, left: -18 }} barCategoryGap="22%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border-soft)" />
          <XAxis
            dataKey="etiqueta"
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }}
          />
          <YAxis
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'var(--color-text-tertiary)' }}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-surface-muted)' }}
            contentStyle={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-soft)',
              borderRadius: 10,
              fontSize: 12,
            }}
            labelStyle={{ color: 'var(--color-text-primary)', fontWeight: 700 }}
          />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11.5 }} />
          {series.map((m, i) => (
            <Bar
              key={m.key}
              dataKey={m.key}
              stackId="m"
              fill={m.color}
              // Solo el segmento superior de la pila lleva las esquinas redondeadas.
              radius={i === series.length - 1 ? [3, 3, 0, 0] : 0}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


// Salidas clínicas (las que llegan a una clasificación de atención).
const CLINICAS = [
  { key: 'teleconsulta', label: 'Teleconsulta', color: '#16a34a' },
  { key: 'presencial', label: 'Presencial', color: '#f59e0b' },
  { key: 'emergencia_911', label: 'Emergencia 911', color: '#dc2626' },
] as const;

interface TriageStatsSectionProps {
  /** Rango [desde, hasta] (ISO yyyy-mm-dd, inclusive) — lo controla el
   *  selector ÚNICO del dashboard, para que TODOS los paneles filtren igual.
   *  (Antes este panel tenía su propio selector, desincronizado del resto.) */
  desde: string;
  hasta: string;
}

export function TriageStatsSection({ desde, hasta }: TriageStatsSectionProps) {
  const [triageDocs, setTriageDocs] = useState<TriageDaily[]>([]);
  const [regionDocs, setRegionDocs] = useState<RegionDaily[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    const unsubT = onSnapshot(
      query(collection(db, 'triage_stats_daily'), where('fecha', '>=', desde), where('fecha', '<=', hasta)),
      (snap) => { setTriageDocs(snap.docs.map((d) => d.data() as TriageDaily)); setLoading(false); },
      () => { setTriageDocs([]); setLoading(false); },
    );
    const unsubR = onSnapshot(
      query(collection(db, 'region_stats_daily'), where('fecha', '>=', desde), where('fecha', '<=', hasta)),
      (snap) => setRegionDocs(snap.docs.map((d) => d.data() as RegionDaily)),
      () => setRegionDocs([]),
    );
    return () => { unsubT(); unsubR(); };
  }, [desde, hasta]);

  // ¿Los buckets `porHora`/`porHoraNivel` cubren TODOS los eventos del rango?
  // No basta con que existan (bug real: el día en que se activó esta métrica
  // tiene eventos de ANTES del deploy sin bucket y de DESPUÉS con bucket — la
  // sola presencia de un bucket parcial apagaba el respaldo y el gráfico
  // mostraba solo un puñado de eventos en vez de los cientos reales del día).
  // Se compara el TOTAL sumado desde los buckets contra el total AUTORITATIVO
  // (`porResultado`/`cambios`/`porNivel`, que se incrementa en cada evento sin
  // importar si también se le escribió bucket horario, así que siempre está
  // completo). Si el bucket no alcanza ese total, está incompleto.
  const resultadoBucketsCompletos = useMemo(() => {
    const autoritativo =
      triageDocs.reduce((a, d) => a + sumaPorResultadoRelevante(d.porResultado), 0) +
      regionDocs.reduce((a, d) => a + (d.cambios || 0), 0);
    const buckets =
      triageDocs.reduce((a, d) => a + sumaPorHoraResultadoRelevante(d.porHora), 0) +
      regionDocs.reduce((a, d) => a + sumaValores(d.porHora), 0);
    return buckets >= autoritativo;
  }, [triageDocs, regionDocs]);
  const nivelBucketsCompletos = useMemo(() => {
    const autoritativo = triageDocs.reduce((a, d) => a + sumaValores(d.porNivel), 0);
    const buckets = triageDocs.reduce((a, d) => a + sumaPorHoraNivel(d.porHoraNivel), 0);
    return buckets >= autoritativo;
  }, [triageDocs]);

  // Respaldo horario: si CUALQUIERA de los dos buckets está incompleto para el
  // rango, se leen los eventos crudos (acotados) y la hora sale de su
  // `creadoEn` — cada gráfica decide (vía `usarBuckets`) si usa el bucket o el
  // respaldo, pero SIEMPRE una fuente completa, nunca mezclando ambas.
  const [crudos, setCrudos] = useState<EventosCrudos | null>(null);
  useEffect(() => {
    if (loading) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (resultadoBucketsCompletos && nivelBucketsCompletos) { setCrudos(null); return; }
    let cancelado = false;
    (async () => {
      try {
        const [evSnap, cbSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'triage_eventos'),
            where('fecha', '>=', desde), where('fecha', '<=', hasta), limit(3000),
          )),
          getDocs(query(
            collection(db, 'region_cambios'),
            where('fecha', '>=', desde), where('fecha', '<=', hasta), limit(3000),
          )),
        ]);
        if (cancelado) return;
        setCrudos({
          eventos: evSnap.docs
            .map((d) => ({
              fecha: String(d.data().fecha ?? ''),
              resultado: String(d.data().resultado ?? ''),
              hora: horaPanama(d.data().creadoEn as Timestamp | undefined) ?? '',
              nivel: typeof d.data().nivel === 'number' ? (d.data().nivel as number) : null,
            }))
            .filter((e) => e.hora !== ''),
          cambios: cbSnap.docs
            .map((d) => ({
              fecha: String(d.data().fecha ?? ''),
              hora: horaPanama(d.data().creadoEn as Timestamp | undefined) ?? '',
            }))
            .filter((c) => c.hora !== ''),
        });
      } catch {
        if (!cancelado) setCrudos(null);
      }
    })();
    return () => { cancelado = true; };
  }, [desde, hasta, loading, triageDocs, regionDocs, resultadoBucketsCompletos, nivelBucketsCompletos]);

  const ag = useMemo(() => {
    const porResultado: Record<string, number> = {};
    const porNivel: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let total = 0;
    for (const d of triageDocs) {
      total += d.total || 0;
      for (const [k, v] of Object.entries(d.porResultado ?? {})) porResultado[k] = (porResultado[k] || 0) + (v || 0);
      for (const [k, v] of Object.entries(d.porNivel ?? {})) {
        const n = Number(k);
        if (n >= 1 && n <= 5) porNivel[n] = (porNivel[n] || 0) + (v || 0);
      }
    }
    const cambiosRegion = regionDocs.reduce((a, d) => a + (d.cambios || 0), 0);
    return { porResultado, porNivel, total, cambiosRegion };
  }, [triageDocs, regionDocs]);

  const g = (k: string) => ag.porResultado[k] || 0;
  const clasificados = g('teleconsulta') + g('presencial') + g('emergencia_911');
  const bloqueados = g('region_no_habilitada') + g('cola_llena');
  const maxNivel = Math.max(1, ...NIVELES.map((n) => ag.porNivel[n.nivel] || 0));

  // Serie temporal (granularidad adaptativa al tamaño del rango).
  const serie = useMemo(
    () => construirSerie(desde, hasta, triageDocs, regionDocs, crudos, resultadoBucketsCompletos),
    [desde, hasta, triageDocs, regionDocs, crudos, resultadoBucketsCompletos],
  );
  const serieTieneDatos = serie.puntos.some((p) =>
    SERIES_TEMPORALES.some((m) => Number(p[m.key] ?? 0) > 0),
  );
  const granularidadLabel =
    serie.granularidad === 'hora' ? 'por hora' : serie.granularidad === 'dia' ? 'por día' : 'por semana';

  // Distribución 0-23h de TODO el rango — segundo gráfico en rangos multi-día
  // ("¿a qué hora del día ocurren los eventos?"). En rangos de 1 día la serie
  // principal YA es horaria, así que no se duplica.
  const distHoras = useMemo(
    () => (serie.granularidad === 'hora' ? null : distribucionHoraria(triageDocs, regionDocs, crudos, resultadoBucketsCompletos)),
    [serie.granularidad, triageDocs, regionDocs, crudos, resultadoBucketsCompletos],
  );
  const distHorasTieneDatos = !!distHoras?.some((p) =>
    SERIES_TEMPORALES.some((m) => Number(p[m.key] ?? 0) > 0),
  );

  // Distribución 0-23h del rango, POR NIVEL de urgencia — mismo principio que
  // distHoras pero para el medidor de acuidad. A diferencia de distHoras, esta
  // SIEMPRE se calcula (incluso con rango de 1 día): "por hora" ya es la
  // única granularidad con sentido para 5 niveles, sin importar el rango.
  const distHorasNivel = useMemo(
    () => distribucionHorariaPorNivel(triageDocs, crudos, nivelBucketsCompletos),
    [triageDocs, crudos, nivelBucketsCompletos],
  );
  const distHorasNivelTieneDatos = distHorasNivel.some((p) =>
    SERIES_NIVELES.some((m) => Number(p[m.key] ?? 0) > 0),
  );

  return (
    <div className={s.card}>
      <div className={s.head}>
        <div className={s.headTitle}>
          <span className={s.headIcon}><Activity size={18} /></span>
          <div className={s.headText}>
            <h2>Triage — clasificación de urgencia</h2>
            <p>Cómo se clasifica y deriva a los pacientes</p>
          </div>
        </div>
        {/* El período lo fija el selector global del dashboard — aquí solo se
            muestra cuál está activo. */}
        <span className={s.rangoActivo}>
          {desde === hasta ? desde : `${desde} → ${hasta}`}
        </span>
      </div>

      <div className={s.body}>
        {loading && ag.total === 0 ? (
          <div className={s.placeholder}>
            <div className={s.placeholderIcon}><Activity size={22} /></div>
            Cargando estadísticas de triage…
          </div>
        ) : ag.total === 0 ? (
          <div className={s.placeholder}>
            <div className={s.placeholderIcon}><Stethoscope size={22} /></div>
            Sin eventos de triage en el rango seleccionado.
          </div>
        ) : (
          <>
            {/* Hero: total clasificados + proporción clínica */}
            <div className={s.hero}>
              <div className={s.heroTotal}>
                <span className={s.heroNum}>{clasificados}</span>
                <span className={s.heroLabel}>Pacientes clasificados</span>
                <span className={s.heroSub}>
                  de <b>{ag.total}</b> eventos de triage en el período
                </span>
              </div>

              <div className={s.propWrap}>
                <span className={s.propTitle}>Derivación clínica</span>
                <div className={s.propBar}>
                  {CLINICAS.map((c) => {
                    const val = g(c.key);
                    const pct = clasificados > 0 ? (val / clasificados) * 100 : 0;
                    if (pct <= 0) return null;
                    return (
                      <div
                        key={c.key}
                        className={s.propSeg}
                        style={{ width: `${pct}%`, background: c.color }}
                        title={`${c.label}: ${val}`}
                      >
                        {pct >= 12 ? val : ''}
                      </div>
                    );
                  })}
                </div>
                <div className={s.propLegend}>
                  {CLINICAS.map((c) => (
                    <span key={c.key} className={s.propLegItem}>
                      <span className={s.propDot} style={{ background: c.color }} />
                      {c.label} <b>{g(c.key)}</b>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Medidor de acuidad por nivel */}
            <div className={s.blockLabel}>
              <AlertTriangle size={13} /> Pacientes por nivel de urgencia
            </div>
            <div className={s.acuity}>
              {NIVELES.map((n) => {
                const val = ag.porNivel[n.nivel] || 0;
                const pct = (val / maxNivel) * 100;
                return (
                  <div key={n.nivel} className={s.acuityRow}>
                    <span className={s.acuityName}>
                      <span className={s.acuityDot} style={{ background: n.color, color: n.color }} />
                      N{n.nivel} · {n.nombre}
                    </span>
                    <span className={s.acuityTrack}>
                      <span
                        className={s.acuityFill}
                        style={{
                          ['--pct' as string]: `${pct}%`,
                          background: `linear-gradient(90deg, ${n.color}, ${n.color}cc)`,
                        }}
                      />
                    </span>
                    <span className={`${s.acuityVal} ${val === 0 ? s.acuityValZero : ''}`}>{val}</span>
                  </div>
                );
              })}
            </div>

            {/* Pacientes por nivel · por hora del día — mismo período que el
                resto del panel (Hoy/7d/30d/rango), agregado 0-23h para ver
                A QUÉ HORA se concentra cada nivel de urgencia. */}
            <div className={s.blockLabel} style={{ marginTop: 16 }}>
              <Clock3 size={13} /> Pacientes por nivel · por hora del día
            </div>
            {distHorasNivelTieneDatos ? (
              <MapaCalor
                columnas={distHorasNivel.map((p) => String(p.etiqueta))}
                filas={filasDeMapa(distHorasNivel, SERIES_NIVELES)}
              />
            ) : (
              <p className={s.serieVacia}>
                Sin desglose horario por nivel disponible para este período (se acumula
                desde que se activó la métrica por hora).
              </p>
            )}

            {/* Secundaria: no completaron / no pudieron entrar / cambios de región */}
            <div className={s.secondary}>
              <div className={s.miniCard}>
                <span className={s.miniIcon} style={{ background: 'rgba(100,116,139,0.14)', color: '#64748b' }}>
                  <LogOut size={18} />
                </span>
                <div className={s.miniBody}>
                  <span className={s.miniVal}>{g('abandono')}</span>
                  <span className={s.miniLabel}>No completaron el triage</span>
                </div>
              </div>

              <div className={s.miniCard}>
                <span className={s.miniIcon} style={{ background: 'rgba(139,92,246,0.14)', color: '#8b5cf6' }}>
                  <MapPinOff size={18} />
                </span>
                <div className={s.miniBody}>
                  <span className={s.miniVal}>{bloqueados}</span>
                  <span className={s.miniLabel}>No pudieron entrar</span>
                  <span className={s.miniHint}>
                    región {g('region_no_habilitada')} · cola {g('cola_llena')}
                  </span>
                </div>
              </div>

              <div className={s.miniCard}>
                <span className={s.miniIcon} style={{ background: 'rgba(37,99,235,0.14)', color: '#2563eb' }}>
                  <Repeat size={18} />
                </span>
                <div className={s.miniBody}>
                  <span className={s.miniVal}>{ag.cambiosRegion}</span>
                  <span className={s.miniLabel}>Cambios de región</span>
                </div>
              </div>

              <div className={s.miniCard}>
                <span className={s.miniIcon} style={{ background: 'rgba(22,163,74,0.14)', color: '#16a34a' }}>
                  <Video size={18} />
                </span>
                <div className={s.miniBody}>
                  <span className={s.miniVal}>{g('teleconsulta')}</span>
                  <span className={s.miniLabel}>Calificaron a teleconsulta</span>
                </div>
              </div>
            </div>

            {/* ── Evolución temporal (granularidad adaptativa, mapa de calor) ── */}
            <div className={s.blockLabel} style={{ marginTop: 22 }}>
              <TrendingUp size={13} /> Evolución en el período · {granularidadLabel}
            </div>
            {serieTieneDatos ? (
              <MapaCalor
                columnas={serie.puntos.map((p) => String(p.etiqueta))}
                filas={filasDeMapa(serie.puntos, SERIES_TEMPORALES)}
              />
            ) : (
              <p className={s.serieVacia}>Sin actividad de estas métricas en el período.</p>
            )}

            {/* ── Distribución 0-23h del rango (solo multi-día): a qué hora del
                   día ocurren los eventos, sin importar la fecha ── */}
            {distHoras && (
              <>
                <div className={s.blockLabel} style={{ marginTop: 22 }}>
                  <Clock3 size={13} /> Distribución por hora del día
                </div>
                {distHorasTieneDatos ? (
                  <GraficaApilada puntos={distHoras} alto={180} />
                ) : (
                  <p className={s.serieVacia}>
                    Sin desglose horario disponible para este período (se acumula desde
                    que se activó la métrica por hora).
                  </p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
